import React, { FC, useContext, useEffect, useState } from 'react'
import { Flex, Stack, Sx, useMantineTheme } from '@mantine/core'
import { useRouter } from 'next/router'
import { Column, RowData } from 'components/ui/ScrollableTable'
import { getBackgroundColor, getBorderColor } from 'config/themeOverride/getColor'
import { CampaignType, useCampaignsLazyQuery } from 'types/domain'
import { calcPercent, formatNumberToThousands } from 'utils'
import { getAfter } from 'utils/graphqlCursor'
import Footer from './Footer'
import ApplicationContext from 'components/business/Layouts/ApplicationLayout/ApplicationContext'
import { isLinkDistribution } from 'utils/applicationUtils'
import { PAGE_SIZE } from 'config/constants'
import Header, { isSystemGroup, SYSTEM_GROUP } from './Header'
import useGroupCache from 'components/business/Campaign/hooks/useGroupCache'
import TableEmpty from 'components/ui/TableEmpty'
import Table from './Table/index'
import useCampaignLayoutCache, { LayoutType } from 'components/business/Campaign/hooks/useCampaignLayoutCache'

type Props = {
  campaignType: CampaignType
  tableColumns: Column[]
  entriesPath: string
}

const pageSize = PAGE_SIZE.LG
const scrollbarOffset = 24

const CampaignList: FC<Props> = ({ campaignType, entriesPath, tableColumns }) => {
  const theme = useMantineTheme()
  const router = useRouter()
  const application = useContext(ApplicationContext).application!
  const isLinkApplication = isLinkDistribution(application)
  const { appId, groupId: groupIdFromUrl } = router.query

  const { setDefaultGroup, getDefaultGroup } = useGroupCache()
  const { setDefaultLayout, getDefaultLayout } = useCampaignLayoutCache()
  const [groupId, setGroupId] = useState<string | null>(null)
  const [keyword, setKeyword] = useState('')
  const [page, setPage] = useState(1)
  const [layout, setLayout] = useState<LayoutType | null>(null)
  const [fetch, { data, previousData, loading, refetch }] = useCampaignsLazyQuery({
    fetchPolicy: 'cache-and-network',
  })

  const fetchCampaigns = (groupId: string | null, keyword: string, page = 1) => {
    setPage(page)
    return fetch({
      variables: {
        id: appId as string,
        pageArguments: { first: pageSize, after: getAfter(page, pageSize).after },
        filter: {
          type: campaignType,
          groupId: isSystemGroup(groupId) ? null : groupId,
          enabled: getEnabled(groupId),
          keyword: keyword || undefined,
        },
      },
    })
  }

  useEffect(() => {
    setLayout(getDefaultLayout(campaignType))

    const groupId = groupIdFromUrl === (SYSTEM_GROUP.ALL as string) ? null : getDefaultGroup(campaignType)
    setGroupId(groupId)
    setDefaultGroup(campaignType, groupId)

    fetchCampaigns(groupId, keyword)
  }, [appId, groupIdFromUrl])

  const handlePageChange = (nextPage: number) => {
    if (nextPage !== page) {
      fetchCampaigns(groupId, keyword, nextPage)
    }
  }

  const handleFilterChange = async (groupId: string | null) => {
    setGroupId(groupId)
    setDefaultGroup(campaignType, groupId)
    await fetchCampaigns(groupId, keyword)
  }

  const handleKeywordChange = (keyword: string) => {
    setKeyword(keyword)
    fetchCampaigns(groupId, keyword)
  }

  const handleLayoutChange = (layout: LayoutType) => {
    setLayout(layout)
    setDefaultLayout(campaignType, layout)
  }

  const getEnabled = (groupId: string | null) => {
    if (groupId === (SYSTEM_GROUP.ENABLED as string)) {
      return true
    }
    if (groupId === (SYSTEM_GROUP.DISABLED as string)) {
      return false
    }
    return null
  }

  const handleRowClick = (data: RowData) => {
    router
      .push({
        pathname: entriesPath,
        query: { appId, campaignId: data.rawData.id },
      })
      .then()
  }

  const campaignRowStyle = (data: RowData) => {
    return {
      cursor: 'pointer',
      overflow: 'hidden',
      borderRadius: '6px',
      backgroundColor: getBackgroundColor('white', theme),
      border: `1px solid ${getBorderColor('gray.1', theme)}`,
      boxShadow: '0px 8px 8px -8px rgba(0, 0, 0, 0.1)',
      position: 'relative',
      gap: 0,
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        width: 4,
        backgroundColor: getBackgroundColor(data.enabled ? 'teal.3' : 'gray.3', theme),
      },
    } as Sx
  }

  const result = data ?? previousData
  const campaigns = result?.application?.id === application.id ? result.application.campaigns : undefined
  const tableData =
    campaigns?.nodes?.map((campaign) => {
      const { name, pv, entriesCount, creator, enabled, urlEnabled, createdAt } = campaign
      return {
        name,
        creator,
        createdAt,
        enabled: isLinkApplication ? urlEnabled : enabled,
        urlEnabled,
        pv: formatNumberToThousands(pv),
        entriesCount: formatNumberToThousands(entriesCount),
        ratio: calcPercent(entriesCount, pv),
        rawData: campaign,
        refetch,
      }
    }) ?? []

  if (layout === null) {
    return null
  }

  return (
    <Stack spacing="lg" className="flex-1" mr={-scrollbarOffset}>
      <Header
        campaignType={campaignType}
        groupId={groupId}
        onGroupChange={handleFilterChange}
        onKeywordChange={handleKeywordChange}
        layout={layout}
        onLayoutChange={handleLayoutChange}
      />
      {tableData.length > 0 ? (
        <Table
          layout={layout}
          data={tableData}
          columns={tableColumns}
          footer={
            <Flex justify="flex-start">
              <Footer
                page={page}
                pageSize={pageSize}
                totalCount={campaigns?.totalCount ?? 0}
                onChange={handlePageChange}
              />
            </Flex>
          }
        />
      ) : (
        campaigns && !loading && <TableEmpty />
      )}
    </Stack>
  )
}

export default CampaignList
